/**
 * SOLID Review
 * S — Acceptable. The profile modal's account management: the nickname edit and the two-step email change (each with its own busy
 *     flag, message and error), the account as the view reads it, and logging out. Each edit is one use case and the rules are the
 *     Domain's (strict-ports FE3); credits and GDPR are their own views' (`BuyCreditsButton`, `GdprRightsTab`).
 * O — Acceptable. A new section is its fields here and its markup in the view, in the shape the two sections share.
 * L — Strong. A view model (`IServiceViewModel`).
 * I — Acceptable. The two sections' actions, and the opening the view hands it.
 * D — Strong. The Application's use cases and view, and the request contexts.
 */
import type { IServiceViewModel } from './IServiceViewModel';
import { ObservableState } from './ObservableState';
import { Reactions } from './Reactions';
import type { ClientRequests } from '../client/ClientRequests';
import { messageFor } from '../client/failureMessage';
import type { IPortClientStateView, TClientViewState } from '../../application/ports/ClientStateView';
import type { IPortClientUseCases, TClientUseCaseInput } from '../../application/ports/ClientUseCases.contract';

/** Opening the profile and signing out carry no command. */
const LOAD_PROFILE: TClientUseCaseInput<'loadProfile'> = {};
const SIGN_OUT: TClientUseCaseInput<'signOut'> = {};

type TEmailStep = 'view' | 'enter-email' | 'enter-code';
type TAccount = TClientViewState['session']['account'];

interface IStateProfileOwn {
  readonly nickname: string;
  readonly savingNick: boolean;
  readonly nickMsg: string | null;
  readonly nickErr: string | null;
  readonly emailStep: TEmailStep;
  readonly newEmail: string;
  readonly code: string;
  readonly emailBusy: boolean;
  readonly emailMsg: string | null;
  readonly emailErr: string | null;
}

/** The modal as its view renders it: its own state over the account the view holds. */
export interface IStateProfile extends IStateProfileOwn {
  readonly displayName: TAccount['displayName'];
  readonly email: TAccount['email'];
  readonly matchCredits: TAccount['matchCredits'];
  readonly registered: boolean;
}

const BLANK: IStateProfileOwn = {
  nickname: '', savingNick: false, nickMsg: null, nickErr: null, emailStep: 'view', newEmail: '', code: '', emailBusy: false, emailMsg: null,
  emailErr: null,
};
const NICK_SAVING: Partial<IStateProfileOwn> = { nickErr: null, nickMsg: null, savingNick: true };
const NICK_SAVED: Partial<IStateProfileOwn> = { nickMsg: 'Nickname updated.' };
const NICK_IDLE: Partial<IStateProfileOwn> = { savingNick: false };
const CODE_SENDING: Partial<IStateProfileOwn> = { emailErr: null, emailMsg: null, emailBusy: true };
const CODE_CHECKING: Partial<IStateProfileOwn> = { emailErr: null, emailBusy: true };
const EMAIL_UPDATED: Partial<IStateProfileOwn> = { emailStep: 'view', emailMsg: 'Email updated.', newEmail: '', code: '' };
const EMAIL_IDLE: Partial<IStateProfileOwn> = { emailBusy: false };
const EMAIL_ENTRY: Partial<IStateProfileOwn> = { emailStep: 'enter-email', emailMsg: null, emailErr: null };
const EMAIL_CANCELLED: Partial<IStateProfileOwn> = { emailStep: 'view', emailErr: null };
const BACK_TO_ADDRESS: Partial<IStateProfileOwn> = { emailStep: 'enter-email', code: '', emailErr: null };

/** What the modal's view hands it: whether it is open. */
type TProfileInputs = readonly [isOpen: boolean];

/** What a commit hands the opening's reaction: the view's input and the account the modal rendered. */
interface IPayloadProfileCommit {
  readonly isOpen: boolean;
  readonly rendered: IStateProfile;
}

/**
 * THE PROFILE MODAL (frontend-framework-oo P3, 2026-10-29, §28.295) — `components/auth/ProfileModal.tsx`'s state, its opening effect
 * and its handlers. Opening resets both sections and pulls the latest profile — a returning player's session only carries the
 * account's id after a reload, so the name may not be known yet; `loadProfile` keeps it and answers it, and a failed read keeps what
 * the view already shows. The nickname typed in the meantime is overwritten by the answer, as it was.
 */
export class ProfileViewModel implements IServiceViewModel<IStateProfile> {
  private readonly state = new ObservableState<IStateProfileOwn>(BLANK);
  private readonly reactions = new Reactions<IPayloadProfileCommit>([{
    deps: (facts) => [facts.isOpen],
    run: ({ isOpen, rendered }) => {
      if (!isOpen) return;
      // Both sections back to their start — a save still in flight keeps its busy flag, as it did.
      const opened: Partial<IStateProfileOwn> = {
        nickMsg: null, nickErr: null, emailStep: 'view', newEmail: '', code: '', emailMsg: null, emailErr: null, nickname: rendered.displayName ?? '',
      };
      this.state.update(opened);
      if (!rendered.registered) return;
      void this.useCases.loadProfile(this.requests.context(), LOAD_PROFILE).then((loaded) => {
        if (!loaded.success || loaded.value === null) return;
        const known: Partial<IStateProfileOwn> = { nickname: loaded.value.displayName };
        this.state.update(known);
      });
    },
  }]);
  private shown: { readonly own: IStateProfileOwn; readonly account: TAccount; readonly profile: IStateProfile } | null = null;

  constructor(
    private readonly useCases: Pick<IPortClientUseCases, 'loadProfile' | 'changeNickname' | 'requestEmailChange' | 'verifyEmailChange' | 'signOut'>,
    private readonly view: IPortClientStateView,
    private readonly requests: ClientRequests,
  ) {}

  private registered(): boolean {
    return this.view.getSnapshot().session.account.kind === 'registered';
  }

  private update(change: Partial<IStateProfileOwn>): void {
    this.state.update(change);
  }

  // ── the nickname ────────────────────────────────────────────────────────────────────────────────────────────────────

  readonly editNickname = (nickname: string): void => {
    const typed: Partial<IStateProfileOwn> = { nickname, nickMsg: null, nickErr: null };
    this.update(typed);
  };

  readonly saveNickname = async (): Promise<void> => {
    if (!this.registered()) return;
    this.update(NICK_SAVING);
    try {
      const command: TClientUseCaseInput<'changeNickname'> = { displayName: this.state.get().nickname };
      const saved = await this.useCases.changeNickname(this.requests.context(), command);
      if (!saved.success) {
        const failed: Partial<IStateProfileOwn> = { nickErr: messageFor(saved.error, 'Could not update nickname.') };
        this.update(failed);
      } else {
        this.update(NICK_SAVED);
      }
    } finally {
      this.update(NICK_IDLE);
    }
  };

  // ── the email change ────────────────────────────────────────────────────────────────────────────────────────────────

  readonly startEmailChange = (): void => this.update(EMAIL_ENTRY);
  readonly cancelEmailChange = (): void => this.update(EMAIL_CANCELLED);
  readonly backToNewEmail = (): void => this.update(BACK_TO_ADDRESS);

  readonly editNewEmail = (newEmail: string): void => {
    const typed: Partial<IStateProfileOwn> = { newEmail, emailErr: null };
    this.update(typed);
  };

  /** Digits only, four at most. */
  readonly editCode = (typed: string): void => {
    const code: Partial<IStateProfileOwn> = { code: typed.replace(/\D/g, '').slice(0, 4), emailErr: null };
    this.update(code);
  };

  readonly sendEmailCode = async (): Promise<void> => {
    if (!this.registered()) return;
    this.update(CODE_SENDING);
    try {
      const command: TClientUseCaseInput<'requestEmailChange'> = { email: this.state.get().newEmail };
      const sent = await this.useCases.requestEmailChange(this.requests.context(), command);
      if (!sent.success) {
        const failed: Partial<IStateProfileOwn> = { emailErr: messageFor(sent.error, 'Could not send the code.') };
        this.update(failed);
      } else {
        const codeSent: Partial<IStateProfileOwn> = { emailStep: 'enter-code', emailMsg: `A 4-digit code was sent to ${sent.value.email}.` };
        this.update(codeSent);
      }
    } finally {
      this.update(EMAIL_IDLE);
    }
  };

  readonly verifyEmail = async (): Promise<void> => {
    if (!this.registered()) return;
    this.update(CODE_CHECKING);
    try {
      const { newEmail, code } = this.state.get();
      const command: TClientUseCaseInput<'verifyEmailChange'> = { email: newEmail, code };
      const verified = await this.useCases.verifyEmailChange(this.requests.context(), command);
      if (!verified.success) {
        const failed: Partial<IStateProfileOwn> = { emailErr: messageFor(verified.error, 'Could not verify the code.') };
        this.update(failed);
      } else {
        this.update(EMAIL_UPDATED);
      }
    } finally {
      this.update(EMAIL_IDLE);
    }
  };

  /** Log out: the session's sign-out, then the modal closes. (An account deleted signs out in its own use case.) */
  readonly signOutAndClose = (close: () => void): void => {
    void this.useCases.signOut(this.requests.context(), SIGN_OUT);
    close();
  };

  // ── the view-model surface ──────────────────────────────────────────────────────────────────────────────────────────

  /** The modal — the same object until its own state or the account changes. */
  getSnapshot(): IStateProfile {
    const own = this.state.get();
    const account = this.view.getSnapshot().session.account;
    if (this.shown !== null && this.shown.own === own && this.shown.account === account) return this.shown.profile;
    const profile: IStateProfile = {
      ...own, displayName: account.displayName, email: account.email, matchCredits: account.matchCredits, registered: account.kind === 'registered',
    };
    this.shown = { own, account, profile };
    return profile;
  }

  subscribe(listener: () => void): () => void {
    const leaveOwn = this.state.subscribe(listener);
    const leaveView = this.view.subscribe(listener);
    return () => {
      leaveOwn();
      leaveView();
    };
  }

  attach(): void {}

  detach(): void {
    this.reactions.stop();
  }

  release(inputs: readonly unknown[], rendered: IStateProfile): void {
    this.reactions.stopChanged(ProfileViewModel.factsOf(inputs, rendered));
  }

  commit(inputs: readonly unknown[], rendered: IStateProfile): void {
    this.reactions.startChanged(ProfileViewModel.factsOf(inputs, rendered));
  }

  private static factsOf(inputs: readonly unknown[], rendered: IStateProfile): IPayloadProfileCommit {
    const [isOpen] = inputs as TProfileInputs;
    return { isOpen, rendered };
  }
}
